'use client'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  Listing,
  RentalFilters,
  SortOption,
  ViewMode,
  Theme,
  Country,
  UiLanguage,
  TravelTimeFilter,
  ReferenceLocation,
  SavedSearch,
  RecentSearch,
  PlannerCard,
  PlannerStatus,
  FilterTag,
  CardDensity,
  SummaryLanguage,
  ListingFreshness,
  DealbreakersConfig,
  NotificationPreferences,
  SharedBoardAccess,
  BoardSharing,
  UserSubscription,
} from './types'
import { DEFAULT_SUBSCRIPTION } from './types'
import type { KeywordsResponse } from './keywords-service'

interface SearchState {
  // Search query
  query: string
  setQuery: (query: string) => void
  
  // Location filters (city and districts)
  selectedCity: string
  setSelectedCity: (city: string) => void
  selectedDistricts: string[]
  setSelectedDistricts: (districts: string[]) => void
  
  // Filters
  filters: RentalFilters
  setFilters: (filters: RentalFilters) => void
  updateFilter: <K extends keyof RentalFilters>(key: K, value: RentalFilters[K]) => void
  clearFilters: () => void
  
  // Reset all search state
  resetSearch: () => void
  
  // Active tags
  activeTags: string[]
  addTag: (tag: string) => void
  removeTag: (tag: string) => void
  toggleTag: (tag: string) => void
  clearTags: () => void
  
  // Highlighted keywords (for listing card highlighting)
  highlightedKeywords: string[]
  setHighlightedKeywords: (keywords: string[]) => void
  
  // Travel time
  travelTime: TravelTimeFilter | null
  setTravelTime: (travelTime: TravelTimeFilter | null) => void

  // Reference location (geocoded origin from travel time / location queries)
  referenceLocation: ReferenceLocation | null
  setReferenceLocation: (loc: ReferenceLocation | null) => void
  
  // Sort
  sort: SortOption
  setSort: (sort: SortOption) => void
  
  // Results
  results: Listing[]
  setResults: (results: Listing[]) => void
  appendResults: (results: Listing[]) => void
  totalCount: number
  setTotalCount: (count: number) => void
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  isLoadingMore: boolean
  setIsLoadingMore: (loading: boolean) => void
  hasSearched: boolean
  setHasSearched: (hasSearched: boolean) => void
  
  // Pagination
  offset: number
  setOffset: (offset: number) => void
  limit: number
  
  // Hidden listings (session only)
  hiddenListings: Set<string>
  hideListing: (id: string) => void
  showListing: (id: string) => void
  showAllHidden: () => void
  
  // Filter count preview (real-time count in filter modal)
  filterCount: number | null
  setFilterCount: (count: number | null) => void
  isCountLoading: boolean
  setIsCountLoading: (loading: boolean) => void
  
  // View mode
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  
  // Expand all descriptions
  expandAllDescriptions: boolean
  setExpandAllDescriptions: (expand: boolean) => void
  toggleExpandAllDescriptions: () => void
}

interface UIState {
  // Theme
  theme: Theme
  setTheme: (theme: Theme) => void
  
  // Country
  country: Country
  setCountry: (country: Country) => void

  // UI language (site chrome localization)
  uiLanguage: UiLanguage
  setUiLanguage: (language: UiLanguage) => void
  
  // Command palette
  commandPaletteOpen: boolean
  setCommandPaletteOpen: (open: boolean) => void
  
  // Gallery
  galleryOpen: boolean
  galleryListingId: string | null
  galleryImageIndex: number
  galleryImages: { url: string; alt_text?: string }[] | null
  openGallery: (listingId: string, imageIndex?: number) => void
  openGalleryWithImages: (images: { url: string; alt_text?: string }[], imageIndex?: number, title?: string) => void
  closeGallery: () => void
  setGalleryImageIndex: (index: number) => void
  
  // Tour
  tourOpen: boolean
  tourCompleted: boolean
  setTourOpen: (open: boolean) => void
  completeTour: () => void

  // Map Provider
  useGoogleMapsMode: boolean
  setUseGoogleMapsMode: (enabled: boolean) => void
}

interface UserState {
  // Saved searches
  savedSearches: SavedSearch[]
  addSavedSearch: (search: SavedSearch) => void
  removeSavedSearch: (id: string) => void
  
  // Recent search history
  recentSearches: RecentSearch[]
  setRecentSearches: (searches: RecentSearch[]) => void
  addRecentSearch: (search: RecentSearch) => void
  removeRecentSearch: (id: string) => void
  clearRecentSearches: () => void
  
  // Saved listings
  savedListingIds: string[]
  setSavedListingIds: (ids: string[]) => void
  mergeSavedListingIds: (serverIds: string[], keepIds?: string[], dropIds?: string[]) => void
  addSavedListing: (id: string) => void
  removeSavedListing: (id: string) => void
  isListingSaved: (id: string) => boolean
  // Count of liked listings that are still LIVE (delisted ones excluded) — the
  // number the /liked page shows. null until that page hydrates it once; the
  // header badge mirrors it. Kept responsive by ±1 on like/unlike.
  likedActiveCount: number | null
  setLikedActiveCount: (n: number) => void
}

// Search store
export const useSearchStore = create<SearchState>()((set) => ({
  // Query
  query: '',
  setQuery: (query) => set({ query }),
  
  // Location filters
  selectedCity: 'Praha',
  setSelectedCity: (selectedCity) => set({ selectedCity }),
  selectedDistricts: [],
  setSelectedDistricts: (selectedDistricts) => set({ selectedDistricts }),
  
  // Filters
  filters: { country: 'CZ', use_total_price: true, transaction_type: 'rent' },
  setFilters: (filters) => set({ filters }),
  updateFilter: (key, value) => set((state) => {
      const nextFilters = { ...state.filters, [key]: value }
      return { filters: nextFilters }
    }),
  clearFilters: () => set({ filters: { country: 'CZ', use_total_price: true, transaction_type: 'rent' }, activeTags: [], highlightedKeywords: [] }),
  
  // Reset all search state
  resetSearch: () => set({
    query: '',
    selectedCity: 'Praha',
    selectedDistricts: [],
    filters: { country: 'CZ', use_total_price: true, transaction_type: 'rent' },
    activeTags: [],
    highlightedKeywords: [],
    travelTime: null,
    referenceLocation: null,
    sort: 'newest',
    results: [],
    totalCount: 0,
    offset: 0,
    isLoading: false,
    isLoadingMore: false,
    hasSearched: false,
    expandAllDescriptions: false,
    filterCount: null,
    isCountLoading: false,
  }),
  
  // Tags
  activeTags: [],
  addTag: (tag) => set((state) => ({
    activeTags: state.activeTags.includes(tag) 
      ? state.activeTags 
      : [...state.activeTags, tag]
  })),
  removeTag: (tag) => set((state) => ({
    activeTags: state.activeTags.filter((t) => t !== tag)
  })),
  toggleTag: (tag) => set((state) => ({
    activeTags: state.activeTags.includes(tag)
      ? state.activeTags.filter((t) => t !== tag)
      : [...state.activeTags, tag]
  })),
  clearTags: () => set({ activeTags: [] }),
  
  // Highlighted keywords
  highlightedKeywords: [],
  setHighlightedKeywords: (highlightedKeywords) => set({ highlightedKeywords }),
  
  // Travel time
  travelTime: null,
  setTravelTime: (travelTime) => set({ travelTime }),

  // Reference location
  referenceLocation: null,
  setReferenceLocation: (referenceLocation) => set({ referenceLocation }),
  
  // Sort
  sort: 'newest',
  setSort: (sort) => set({ sort }),
  
  // Results
  results: [],
  setResults: (results) => set({ results, offset: 0 }),
  appendResults: (newResults) => set((state) => ({ 
    results: [...state.results, ...newResults] 
  })),
  totalCount: 0,
  setTotalCount: (totalCount) => set({ totalCount }),
  isLoading: false,
  setIsLoading: (isLoading) => set({ isLoading }),
  isLoadingMore: false,
  setIsLoadingMore: (isLoadingMore) => set({ isLoadingMore }),
  hasSearched: false,
  setHasSearched: (hasSearched) => set({ hasSearched }),
  
  // Pagination
  offset: 0,
  setOffset: (offset) => set({ offset }),
  limit: 20,
  
  // Hidden listings
  hiddenListings: new Set(),
  hideListing: (id) => set((state) => ({
    hiddenListings: new Set([...state.hiddenListings, id])
  })),
  showListing: (id) => set((state) => {
    const newHidden = new Set(state.hiddenListings)
    newHidden.delete(id)
    return { hiddenListings: newHidden }
  }),
  showAllHidden: () => set({ hiddenListings: new Set() }),
  
  // Filter count preview
  filterCount: null,
  setFilterCount: (filterCount) => set({ filterCount }),
  isCountLoading: false,
  setIsCountLoading: (isCountLoading) => set({ isCountLoading }),
  
  // View mode
  viewMode: 'list',
  setViewMode: (viewMode) => set({ viewMode }),
  
  // Expand all descriptions
  expandAllDescriptions: false,
  setExpandAllDescriptions: (expandAllDescriptions) => set({ expandAllDescriptions }),
  toggleExpandAllDescriptions: () => set((state) => ({ 
    expandAllDescriptions: !state.expandAllDescriptions 
  })),
}))

// UI store with persistence
export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Theme
      theme: 'system',
      setTheme: (theme) => set({ theme }),
      
      // Country
      country: 'CZ',
      setCountry: (country) => set({ country }),

      // UI language
      uiLanguage: 'cs',
      setUiLanguage: (uiLanguage) => set({ uiLanguage }),
      
      // Command palette
      commandPaletteOpen: false,
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
      
      // Gallery
      galleryOpen: false,
      galleryListingId: null,
      galleryImageIndex: 0,
      galleryImages: null,
      openGallery: (listingId, imageIndex = 0) => set({
        galleryOpen: true,
        galleryListingId: listingId,
        galleryImageIndex: imageIndex,
        galleryImages: null,
      }),
      openGalleryWithImages: (images, imageIndex = 0, title) => set({
        galleryOpen: true,
        galleryListingId: title || null,
        galleryImageIndex: imageIndex,
        galleryImages: images,
      }),
      closeGallery: () => set({
        galleryOpen: false,
        galleryListingId: null,
        galleryImageIndex: 0,
        galleryImages: null,
      }),
      setGalleryImageIndex: (index) => set({ galleryImageIndex: index }),
      
      // Tour
      tourOpen: false,
      tourCompleted: false,
      setTourOpen: (open) => set({ tourOpen: open }),
      completeTour: () => set({ tourCompleted: true, tourOpen: false }),

      // Map Provider (experimental Google Maps mode)
      useGoogleMapsMode: false,
      setUseGoogleMapsMode: (enabled) => set({ useGoogleMapsMode: enabled }),
    }),
    {
      name: 'roostr-ui',
      partialize: (state) => ({
        theme: state.theme,
        country: state.country,
        uiLanguage: state.uiLanguage,
        tourCompleted: state.tourCompleted,
        useGoogleMapsMode: state.useGoogleMapsMode,
      }),
    }
  )
)

// User store with persistence
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      savedSearches: [],
      addSavedSearch: (search) => set((state) => ({
        savedSearches: [...state.savedSearches, search]
      })),
      removeSavedSearch: (id) => set((state) => ({
        savedSearches: state.savedSearches.filter((s) => s.id !== id)
      })),
      
      // Recent search history
      recentSearches: [],
      setRecentSearches: (recentSearches) => set({ recentSearches }),
      addRecentSearch: (search) => set((state) => {
        // Deduplicate by displayLabel — bump to top if already exists
        const filtered = state.recentSearches.filter(
          (s) => s.displayLabel !== search.displayLabel
        )
        return {
          recentSearches: [search, ...filtered].slice(0, 20),
        }
      }),
      removeRecentSearch: (id) => set((state) => ({
        recentSearches: state.recentSearches.filter((s) => s.id !== id)
      })),
      clearRecentSearches: () => set({ recentSearches: [] }),
      
      // Saved listings
      savedListingIds: [],
      setSavedListingIds: (ids) => set({ savedListingIds: ids }),
      // Server-sync merge: (serverIds ∪ keepIds) − dropIds, NOT a plain
      // replace. keepIds/dropIds are the ids optimistically saved/unsaved this
      // session (tracked by components/listings/use-save-listing.ts), so a
      // slow getSavedListingIds response can't clobber in-flight likes.
      // Tradeoff: for those in-session ids only, an unsave made on another
      // device mid-session isn't reflected until the next sign-in sync.
      mergeSavedListingIds: (serverIds, keepIds = [], dropIds = []) => set(() => {
        const drop = new Set(dropIds)
        const seen = new Set<string>()
        const merged: string[] = []
        for (const id of [...serverIds, ...keepIds]) {
          if (!drop.has(id) && !seen.has(id)) {
            seen.add(id)
            merged.push(id)
          }
        }
        return { savedListingIds: merged }
      }),
      likedActiveCount: null,
      setLikedActiveCount: (n) => set({ likedActiveCount: n }),
      addSavedListing: (id) => set((state) => {
        if (state.savedListingIds.includes(id)) return state
        return {
          savedListingIds: [...state.savedListingIds, id],
          // A freshly-liked listing is live → keep the active count in step.
          likedActiveCount: state.likedActiveCount == null ? null : state.likedActiveCount + 1,
        }
      }),
      removeSavedListing: (id) => set((state) => {
        if (!state.savedListingIds.includes(id)) return state
        return {
          savedListingIds: state.savedListingIds.filter((listingId) => listingId !== id),
          likedActiveCount:
            state.likedActiveCount == null ? null : Math.max(0, state.likedActiveCount - 1),
        }
      }),
      isListingSaved: (id) => get().savedListingIds.includes(id),
    }),
    {
      name: 'roostr-user',
    }
  )
)

// Planner store state interface
interface PlannerState {
  // Cards
  cards: PlannerCard[]
  setCards: (cards: PlannerCard[]) => void
  
  // Loading state
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  isSyncing: boolean
  setIsSyncing: (syncing: boolean) => void
  
  // Undo stack
  undoStack: PlannerCard[][]
  pushUndo: () => void
  undo: () => boolean
  clearUndoStack: () => void
  
  // Card operations
  addCard: (card: PlannerCard) => void
  updateCard: (listingId: string, updates: Partial<PlannerCard>) => void
  removeCard: (listingId: string) => void
  moveCard: (listingId: string, newStatus: PlannerStatus) => void
  reorderCards: (activeId: string, overId: string) => void
  moveCardUp: (listingId: string) => void
  moveCardDown: (listingId: string) => void
  
  // Rating
  setRating: (listingId: string, rating: 1 | 2 | 3 | null) => void
  
  // Notes
  setNotes: (listingId: string, notes: string) => void
  
  // Tags
  addTag: (listingId: string, tag: string) => void
  removeTag: (listingId: string, tag: string) => void
  
  // Budget
  setBudgetMax: (listingId: string, budgetMax: number | null) => void
  
  // Selection (for comparison)
  selectedForComparison: string[]
  toggleComparisonSelection: (listingId: string) => void
  clearComparisonSelection: () => void
  
  // View mode
  plannerViewMode: 'kanban' | 'comparison' | 'analytics'
  setPlannerViewMode: (mode: 'kanban' | 'comparison' | 'analytics') => void
  
  // Shared board state
  currentBoardOwnerUid: string | null  // null = personal board, string = viewing someone else's board
  setCurrentBoardOwner: (uid: string | null) => void
  sharedBoardsAccess: SharedBoardAccess[]
  setSharedBoardsAccess: (access: SharedBoardAccess[]) => void
  boardSharing: BoardSharing
  setBoardSharing: (sharing: BoardSharing) => void
  
  // Helpers
  getCardByListingId: (listingId: string) => PlannerCard | undefined
  getCardsByStatus: (status: PlannerStatus) => PlannerCard[]
  isViewingSharedBoard: () => boolean
}

// Planner store
export const usePlannerStore = create<PlannerState>()(
  persist(
    (set, get) => ({
      // Cards
      cards: [],
      setCards: (cards) => set({ cards }),
      
      // Loading state
      isLoading: false,
      setIsLoading: (isLoading) => set({ isLoading }),
      isSyncing: false,
      setIsSyncing: (isSyncing) => set({ isSyncing }),
      
      // Undo stack (max 10 states)
      undoStack: [],
      pushUndo: () => set((state) => ({
        undoStack: [...state.undoStack.slice(-9), [...state.cards]]
      })),
      undo: () => {
        const { undoStack, cards } = get()
        if (undoStack.length === 0) return false
        
        const previousState = undoStack[undoStack.length - 1]
        set({
          cards: previousState,
          undoStack: undoStack.slice(0, -1)
        })
        return true
      },
      clearUndoStack: () => set({ undoStack: [] }),
      
      // Card operations
      addCard: (card) => set((state) => {
        if (state.cards.some(c => c.listingId === card.listingId)) {
          return state // Already exists
        }
        return { cards: [...state.cards, card] }
      }),
      
      updateCard: (listingId, updates) => set((state) => ({
        cards: state.cards.map(c => 
          c.listingId === listingId 
            ? { ...c, ...updates, updatedAt: new Date().toISOString() }
            : c
        )
      })),
      
      removeCard: (listingId) => set((state) => ({
        cards: state.cards.filter(c => c.listingId !== listingId),
        selectedForComparison: state.selectedForComparison.filter(id => id !== listingId)
      })),
      
      moveCard: (listingId, newStatus) => {
        const { pushUndo, updateCard } = get()
        pushUndo()
        updateCard(listingId, { status: newStatus })
      },
      
      reorderCards: (activeId, overId) => {
        set((state) => {
          const oldIndex = state.cards.findIndex(c => c.listingId === activeId)
          const newIndex = state.cards.findIndex(c => c.listingId === overId)
          
          if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
            return state
          }
          
          const newCards = [...state.cards]
          const [removed] = newCards.splice(oldIndex, 1)
          newCards.splice(newIndex, 0, removed)
          
          return { cards: newCards }
        })
      },
      
      moveCardUp: (listingId) => {
        set((state) => {
          const card = state.cards.find(c => c.listingId === listingId)
          if (!card) return state
          
          // Get cards in the same column
          const columnCards = state.cards.filter(c => c.status === card.status)
          const cardIndex = columnCards.findIndex(c => c.listingId === listingId)
          
          if (cardIndex <= 0) return state // Already at top
          
          // Find the card above in the same column
          const cardAbove = columnCards[cardIndex - 1]
          
          // Swap positions in the main cards array
          const newCards = [...state.cards]
          const currentIdx = newCards.findIndex(c => c.listingId === listingId)
          const aboveIdx = newCards.findIndex(c => c.listingId === cardAbove.listingId)
          
          ;[newCards[currentIdx], newCards[aboveIdx]] = [newCards[aboveIdx], newCards[currentIdx]]
          
          return { cards: newCards }
        })
      },
      
      moveCardDown: (listingId) => {
        set((state) => {
          const card = state.cards.find(c => c.listingId === listingId)
          if (!card) return state
          
          // Get cards in the same column
          const columnCards = state.cards.filter(c => c.status === card.status)
          const cardIndex = columnCards.findIndex(c => c.listingId === listingId)
          
          if (cardIndex >= columnCards.length - 1) return state // Already at bottom
          
          // Find the card below in the same column
          const cardBelow = columnCards[cardIndex + 1]
          
          // Swap positions in the main cards array
          const newCards = [...state.cards]
          const currentIdx = newCards.findIndex(c => c.listingId === listingId)
          const belowIdx = newCards.findIndex(c => c.listingId === cardBelow.listingId)
          
          ;[newCards[currentIdx], newCards[belowIdx]] = [newCards[belowIdx], newCards[currentIdx]]
          
          return { cards: newCards }
        })
      },
      
      // Rating
      setRating: (listingId, rating) => {
        const { updateCard } = get()
        updateCard(listingId, { rating })
      },
      
      // Notes
      setNotes: (listingId, notes) => {
        const { updateCard } = get()
        updateCard(listingId, { notes })
      },
      
      // Tags
      addTag: (listingId, tag) => set((state) => ({
        cards: state.cards.map(c => 
          c.listingId === listingId && !c.customTags.includes(tag)
            ? { ...c, customTags: [...c.customTags, tag], updatedAt: new Date().toISOString() }
            : c
        )
      })),
      
      removeTag: (listingId, tag) => set((state) => ({
        cards: state.cards.map(c => 
          c.listingId === listingId
            ? { ...c, customTags: c.customTags.filter(t => t !== tag), updatedAt: new Date().toISOString() }
            : c
        )
      })),
      
      // Budget
      setBudgetMax: (listingId, budgetMax) => {
        const { updateCard } = get()
        updateCard(listingId, { budgetMax })
      },
      
      // Selection for comparison
      selectedForComparison: [],
      toggleComparisonSelection: (listingId) => set((state) => {
        if (state.selectedForComparison.includes(listingId)) {
          return { selectedForComparison: state.selectedForComparison.filter(id => id !== listingId) }
        }
        // Max 6 selections
        if (state.selectedForComparison.length >= 10) {
          return state
        }
        return { selectedForComparison: [...state.selectedForComparison, listingId] }
      }),
      clearComparisonSelection: () => set({ selectedForComparison: [] }),
      
      // View mode
      plannerViewMode: 'kanban',
      setPlannerViewMode: (plannerViewMode) => set({ plannerViewMode }),
      
      // Shared board state
      currentBoardOwnerUid: null,
      setCurrentBoardOwner: (currentBoardOwnerUid) => set({ currentBoardOwnerUid }),
      sharedBoardsAccess: [],
      setSharedBoardsAccess: (sharedBoardsAccess) => set({ sharedBoardsAccess }),
      boardSharing: { isShared: false, collaborators: [] },
      setBoardSharing: (boardSharing) => set({ boardSharing }),
      
      // Helpers
      getCardByListingId: (listingId) => get().cards.find(c => c.listingId === listingId),
      getCardsByStatus: (status) => get().cards.filter(c => c.status === status),
      isViewingSharedBoard: () => get().currentBoardOwnerUid !== null,
    }),
    {
      name: 'roostr-planner',
      version: 3,  // Increment for shared board state additions
      migrate: (persistedState) => persistedState as any,
      partialize: (state) => ({
        cards: state.cards,
        plannerViewMode: state.plannerViewMode,
      }),
    }
  )
)

// Keywords store - loads once at app startup and caches globally
interface KeywordsState {
  keywords: KeywordsResponse | null
  keywordsLanguage: UiLanguage | null
  filterTags: FilterTag[]
  isLoading: boolean
  error: string | null
  loadKeywords: (language?: UiLanguage) => Promise<void>
  setKeywords: (keywords: KeywordsResponse) => void
  setFilterTags: (tags: FilterTag[]) => void
}

export const useKeywordsStore = create<KeywordsState>()(
  persist(
    (set, get) => ({
      keywords: null,
      keywordsLanguage: null,
      filterTags: [],
      isLoading: false,
      error: null,
      
      loadKeywords: async (language) => {
        // Prevent concurrent fetches (but allow refresh when not loading)
        const state = get()
        const activeLanguage = (language ?? useUIStore.getState().uiLanguage ?? 'cs') as UiLanguage
        if (state.keywords && state.keywordsLanguage === activeLanguage && state.filterTags.length > 0) {
          return
        }
        if (state.isLoading) {
          return
        }
        
        set({ isLoading: true, error: null })
        
        try {
          const response = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080'}/api/v1/keywords?lang=${activeLanguage}`
          )
          
          if (!response.ok) {
            throw new Error(`Failed to fetch keywords: ${response.statusText}`)
          }
          
          const data: KeywordsResponse = await response.json()
          set({ keywords: data, keywordsLanguage: activeLanguage, isLoading: false })
          
          // Also build filter tags
          const { buildFilterTags } = await import('./constants')
          const tags = await buildFilterTags(data, activeLanguage)
          set({ filterTags: tags })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          console.error('Failed to load keywords:', errorMessage)
          set({ error: errorMessage, isLoading: false })
        }
      },
      
      setKeywords: (keywords) => set({ keywords }),
      setFilterTags: (filterTags) => set({ filterTags }),
    }),
    {
      name: 'roostr-keywords',
      version: 2,
      migrate: (persistedState) => persistedState as any,
      partialize: (state) => ({
        keywords: state.keywords,
        keywordsLanguage: state.keywordsLanguage,
        filterTags: state.filterTags,
      }),
      // Rehydrate check: skip if version in storage is different from API
      onRehydrateStorage: () => (state) => {
        if (state?.keywords) {
          // Check if cached keywords are older than 24 hours
          const cacheDate = new Date(state.keywords.version)
          const now = new Date()
          const hoursSinceCache = (now.getTime() - cacheDate.getTime()) / (1000 * 60 * 60)
          
          if (hoursSinceCache > 24) {
            // Stale, reload
            state.loadKeywords()
          }
        }
      },
    }
  )
)

// Settings store — holds user preferences synced from Firebase
interface SettingsState {
  // Appearance
  cardDensity: CardDensity
  setCardDensity: (density: CardDensity) => void
  cardSize: number
  setCardSize: (size: number) => void
  // Map view's side panel — user-resizable and collapsible (persisted).
  mapPanelWidth: number
  setMapPanelWidth: (px: number) => void
  mapPanelOpen: boolean
  setMapPanelOpen: (open: boolean) => void
  defaultViewMode: ViewMode
  setDefaultViewMode: (mode: ViewMode) => void

  // Search defaults
  defaultCity: string
  setDefaultCity: (city: string) => void
  defaultSort: SortOption
  setDefaultSort: (sort: SortOption) => void
  defaultPriceMode: boolean
  setDefaultPriceMode: (mode: boolean) => void
  listingFreshness: ListingFreshness
  setListingFreshness: (freshness: ListingFreshness) => void
  dealbreakers: DealbreakersConfig
  setDealbreakers: (config: DealbreakersConfig) => void
  // AI summary language
  aiSummaryLanguage: SummaryLanguage
  setAiSummaryLanguage: (lang: SummaryLanguage) => void

  // Notifications
  notifications: NotificationPreferences
  setNotifications: (prefs: NotificationPreferences) => void
  updateNotification: <K extends keyof NotificationPreferences>(key: K, value: NotificationPreferences[K]) => void

  // Privacy
  searchHistoryEnabled: boolean
  setSearchHistoryEnabled: (enabled: boolean) => void

  // Subscription
  subscription: UserSubscription
  setSubscription: (sub: UserSubscription) => void

  // Hydration
  isHydrated: boolean
  hydrateFromFirebase: (prefs: Partial<SettingsState>) => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      // Appearance
      cardDensity: 'comfortable',
      setCardDensity: (cardDensity) => set({ cardDensity }),
      cardSize: 380,
      setCardSize: (cardSize) => set({ cardSize }),
      mapPanelWidth: 440,
      setMapPanelWidth: (mapPanelWidth) => set({ mapPanelWidth }),
      mapPanelOpen: true,
      setMapPanelOpen: (mapPanelOpen) => set({ mapPanelOpen }),
      defaultViewMode: 'list',
      setDefaultViewMode: (defaultViewMode) => set({ defaultViewMode }),

      // Search defaults
      defaultCity: 'Praha',
      setDefaultCity: (defaultCity) => set({ defaultCity }),
      defaultSort: 'newest',
      setDefaultSort: (defaultSort) => set({ defaultSort }),
      defaultPriceMode: true,
      setDefaultPriceMode: (defaultPriceMode) => set({ defaultPriceMode }),
      listingFreshness: null,
      setListingFreshness: (listingFreshness) => set({ listingFreshness }),
      dealbreakers: { mustHave: [] },
      setDealbreakers: (dealbreakers) => set({ dealbreakers }),
      // AI summary language (Czech default — matches the source-language listings)
      aiSummaryLanguage: 'cs',
      setAiSummaryLanguage: (aiSummaryLanguage) => set({ aiSummaryLanguage }),

      // Notifications
      notifications: {
        email: true,
        push: false,
        whatsapp: false,
        frequency: 'daily',
        quietHoursEnabled: false,
        quietHoursStart: '22:00',
        quietHoursEnd: '08:00',
      },
      setNotifications: (notifications) => set({ notifications }),
      updateNotification: (key, value) => set((state) => ({
        notifications: { ...state.notifications, [key]: value },
      })),

      // Privacy
      searchHistoryEnabled: true,
      setSearchHistoryEnabled: (searchHistoryEnabled) => set({ searchHistoryEnabled }),

      // Subscription
      subscription: { ...DEFAULT_SUBSCRIPTION },
      setSubscription: (subscription) => set({ subscription }),

      // Hydration
      isHydrated: false,
      hydrateFromFirebase: (prefs) => set({ ...prefs, isHydrated: true }),
    }),
    {
      name: 'roostr-settings',
      version: 1,
      migrate: (persistedState) => persistedState as any,
      partialize: (state) => ({
        cardDensity: state.cardDensity,
        mapPanelWidth: state.mapPanelWidth,
        mapPanelOpen: state.mapPanelOpen,
        defaultViewMode: state.defaultViewMode,
        defaultCity: state.defaultCity,
        defaultSort: state.defaultSort,
        defaultPriceMode: state.defaultPriceMode,
        listingFreshness: state.listingFreshness,
        dealbreakers: state.dealbreakers,
        aiSummaryLanguage: state.aiSummaryLanguage,
        notifications: state.notifications,
        searchHistoryEnabled: state.searchHistoryEnabled,
        subscription: state.subscription,
      }),
    }
  )
)
